import { e2eMockApiFetch } from '@/lib/api.e2e'
import { isE2EMockApiEnabled } from '@/lib/e2e'
import { storage } from '@/lib/storage'

const BASE_URL_ENV = process.env.EXPO_PUBLIC_API_URL
const E2E_MOCK_API = isE2EMockApiEnabled()

if (!BASE_URL_ENV && !E2E_MOCK_API) {
  throw new Error('Missing EXPO_PUBLIC_API_URL environment variable')
}

const BASE_URL = BASE_URL_ENV ?? 'https://e2e.invalid'
const NETWORK_ERROR_STATUS = 0
const NETWORK_ERROR_CODE = 'network_error'
const NETWORK_ERROR_MESSAGE =
  "We couldn't reach Turbina right now. Please check your internet connection and try again."
const TIMEOUT_ERROR_MESSAGE = 'The request took too long. Please try again.'

const _ts = () => new Date().toISOString()

// In-memory cache so we never hit SecureStore on every API call.
// Populated by AuthContext after Cognito sign-in / session restore;
// cleared on sign-out.
let _accessToken: string | null = null

export function setAccessToken(token: string | null): void {
  _accessToken = token
}

async function getAccessToken(): Promise<string | null> {
  if (_accessToken !== null) return _accessToken
  // Fallback: read from persistent storage (e.g. first call after a cold
  // app launch where AuthContext hasn't finished restoring yet).
  const token = await storage.get('turbina_access_token')
  _accessToken = token
  return token
}

function isErrorWithStatus(err: unknown): err is { status: number; message?: string } {
  return Boolean(
    err &&
    typeof err === 'object' &&
    'status' in err &&
    typeof (err as { status: unknown }).status === 'number'
  )
}

function toFriendlyNetworkError(err: unknown): Error & { status: number; code: string } {
  const isAbortError = err instanceof Error && err.name === 'AbortError'
  const wrapped = new Error(
    isAbortError ? TIMEOUT_ERROR_MESSAGE : NETWORK_ERROR_MESSAGE
  ) as Error & {
    status: number
    code: string
  }
  wrapped.status = NETWORK_ERROR_STATUS
  wrapped.code = NETWORK_ERROR_CODE
  return wrapped
}

async function parseJsonOrUndefined<T>(res: Response): Promise<T | undefined> {
  if (res.status === 204 || (res.status === 201 && res.headers.get('content-length') === '0'))
    return undefined
  return (await res.json()) as T
}

interface RetryRequest {
  path: string
  options: RequestInit
  headers: Record<string, string>
}

function createHttpError(status: number, message: string): Error & { status: number } {
  const err = new Error(message) as Error & { status: number }
  err.status = status
  return err
}

function buildJsonHeaders(
  token: string | null,
  providedHeaders?: RequestInit['headers']
): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(providedHeaders as Record<string, string>),
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

async function fetchWithFriendlyNetworkError(
  path: string,
  options: RequestInit,
  headers: Record<string, string>
): Promise<Response> {
  try {
    return await fetch(`${BASE_URL}${path}`, { ...options, headers })
  } catch (err: unknown) {
    throw toFriendlyNetworkError(err)
  }
}

async function refreshAndRetry<T>(req: RetryRequest): Promise<T> {
  const { path, options, headers } = req

  const { refreshStoredTokens } = await import('@/lib/cognito')
  const refreshed = await refreshStoredTokens()
  _accessToken = refreshed.accessToken
  const retryHeaders = { ...headers, Authorization: `Bearer ${refreshed.accessToken}` }
  const retryRes = await fetchWithFriendlyNetworkError(path, options, retryHeaders)
  if (!retryRes.ok) {
    const retryText = await retryRes.text()
    throw createHttpError(retryRes.status, retryText || `HTTP ${retryRes.status}`)
  }
  const data = await parseJsonOrUndefined<T>(retryRes)
  return data as T
}

async function handleFailedResponse<T>(res: Response, req: RetryRequest): Promise<T> {
  if (res.status !== 401) {
    const text = await res.text()
    throw createHttpError(res.status, text || `HTTP ${res.status}`)
  }

  try {
    return await refreshAndRetry<T>(req)
  } catch (err: unknown) {
    if (isErrorWithStatus(err)) {
      throw err
    }
    _accessToken = null
    throw createHttpError(401, 'unauthorized')
  }
}

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await getAccessToken()

  if (E2E_MOCK_API) {
    return e2eMockApiFetch<T>(path, options, token)
  }

  const headers = buildJsonHeaders(token, options.headers)
  const res = await fetchWithFriendlyNetworkError(path, options, headers)

  if (!res.ok) {
    return handleFailedResponse<T>(res, { path, options, headers })
  }

  return (await parseJsonOrUndefined<T>(res)) as T
}

export interface ApiUser {
  id: string
  cognito_id: string
  email: string
  name: string
  surname: string
  phone: string | null
  instagram: string | null
  onboarding_goal: string
  onboarding_destination: string
  onboarding_timeline: string
  origin_city: string
  origin_country: string
  age_range: string
  savings_status: string
  current_situation: string
  application_status: string
  application_submission_type: string
  application_video_url: string
  application_written_why_trip: string
  application_written_limitations: string
  application_written_experience_goal: string
  application_written_why_generation_one: string
  application_submitted_at: string | null
  departure: string | null
  created_at: string
}

export interface OnboardingData {
  goal?: string
  destination?: string
  timeline?: string
  origin_city?: string
  origin_country?: string
  age_range?: string
  savings_status?: string
  current_situation?: string
}

export function getMe(): Promise<ApiUser> {
  return apiFetch<ApiUser>('/me')
}

export function createUser(data: {
  email: string
  name?: string
  surname?: string
  phone?: string
  onboarding_session_id?: string
}): Promise<ApiUser> {
  return apiFetch<ApiUser>('/users', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

type StatsPayload = {
  user_count: number
  max_users: number
  mapbox_max_private_calls: number
  feature_dual_application_paths?: boolean
}

const STATS_CACHE_TTL_MS = 60_000

let _statsCache: {
  data: StatsPayload
  cachedAtMs: number
} | null = null

export async function getStats(): Promise<StatsPayload> {
  const nowMs = Date.now()
  if (
    !E2E_MOCK_API &&
    _statsCache !== null &&
    nowMs - _statsCache.cachedAtMs < STATS_CACHE_TTL_MS
  ) {
    return _statsCache.data
  }
  const data = await apiFetch<StatsPayload>('/stats')
  if (!E2E_MOCK_API) {
    _statsCache = {
      data,
      cachedAtMs: nowMs,
    }
  }
  return data
}

export async function initMapbox(isPrivate: boolean): Promise<boolean> {
  const data = await apiFetch<{ allowed: boolean }>(`/mapbox/init?private=${isPrivate}`, {
    method: 'POST',
  })
  return data.allowed
}

export function joinWaitlist(
  email: string,
  onboardingId?: string,
  onboarding?: {
    onboarding_goal?: string
    onboarding_destination?: string
    onboarding_timeline?: string
    origin_city?: string
    origin_country?: string
    age_range?: string
    savings_status?: string
    current_situation?: string
  }
): Promise<void> {
  return apiFetch<void>('/waitlist', {
    method: 'POST',
    body: JSON.stringify({
      email,
      onboarding_id: onboardingId ?? undefined,
      ...onboarding,
    }),
  })
}

export function patchOnboardingSession(sessionId: string, step: OnboardingData): Promise<void> {
  return apiFetch<void>(`/onboarding/sessions/${sessionId}`, {
    method: 'PATCH',
    body: JSON.stringify(step),
  })
}

export function claimOnboardingSession(sessionId: string): Promise<ApiUser> {
  return apiFetch<ApiUser>(`/onboarding/sessions/${sessionId}/claim`, {
    method: 'POST',
  })
}

export function patchMe(
  data: Partial<{
    application_submission_type: string
    application_video_url: string
    application_written_why_trip: string
    application_written_limitations: string
    application_written_experience_goal: string
    application_written_why_generation_one: string
    name: string
    surname: string
    phone: string
    instagram: string
    onboarding_goal: string
    onboarding_destination: string
    onboarding_timeline: string
    age_range: string
    origin_city: string
    origin_country: string
    savings_status: string
    current_situation: string
    departure: string
  }>
): Promise<ApiUser> {
  return apiFetch<ApiUser>('/me', {
    method: 'PATCH',
    body: JSON.stringify(data),
  }).then(() => getMe())
}

export interface ApiMyCohortResponse {
  cohort: {
    id: string
    name: string
    destination: string
    departure_date: string
    status: string
  }
  member_status: string
  created_at: string
  updated_at: string | null
}

export function getMyCohort(): Promise<ApiMyCohortResponse> {
  return apiFetch<ApiMyCohortResponse>('/me/cohort')
}

// ─── Campaign ────────────────────────────────────────────────────────────────

export interface ApiCampaign {
  id: string
  user_id: string
  title: string
  youtube_url: string | null
  status: string
  is_published: boolean
  story_who_are_you: string
  short_id: string | null
  created_at: string
  updated_at: string
}

export interface ApiPublicCampaignResponse {
  short_id: string
  title: string
  name: string
  surname: string
  youtube_url: string | null
  story_who_are_you: string
  phone: string | null
  instagram: string | null
  services: ApiCampaignService[]
  goal_total: number
  savings_amount: number
  departure: string | null
}

export interface ApiCampaignService {
  id: string
  user_id: string
  project_id: string | null
  title: string
  description: string | null
  price: number | null
  currency: string
  status: string
  created_at: string
  updated_at: string
}

export interface ApiCampaignCompletion {
  required_prompts_completed: boolean
  has_video: boolean
  has_service: boolean
  is_complete: boolean
  missing_fields: string[]
}

export interface ApiMyCampaignResponse {
  campaign: ApiCampaign | null
  services: ApiCampaignService[]
  completion: ApiCampaignCompletion
}

export function getMyCampaign(): Promise<ApiMyCampaignResponse> {
  return apiFetch<ApiMyCampaignResponse>('/me/campaign')
}

export function upsertMyCampaign(
  data: Partial<{
    title: string
    youtube_url: string
    story_who_are_you: string
    short_id: string
  }>
): Promise<ApiMyCampaignResponse> {
  return apiFetch<ApiMyCampaignResponse>('/me/campaign', {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export function publishMyCampaign(): Promise<ApiMyCampaignResponse> {
  return apiFetch<ApiMyCampaignResponse>('/me/campaign/publish', {
    method: 'POST',
  })
}

export function getMyCampaignServices(): Promise<ApiCampaignService[]> {
  return apiFetch<ApiCampaignService[]>('/me/campaign/services')
}

export function createMyCampaignService(data: {
  title: string
  description?: string
  price?: number
  currency?: string
}): Promise<ApiCampaignService> {
  return apiFetch<ApiCampaignService>('/me/campaign/services', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export function patchMyCampaignService(
  id: string,
  data: Partial<{
    title: string
    description: string
    price: number
    currency: string
    status: string
  }>
): Promise<ApiCampaignService> {
  return apiFetch<ApiCampaignService>(`/me/campaign/services/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export function deleteMyCampaignService(id: string): Promise<void> {
  return apiFetch<void>(`/me/campaign/services/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export async function unsubscribeEmail(email: string, token: string): Promise<void> {
  let res: Response
  try {
    res = await fetch(`${BASE_URL}/unsubscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, token }),
    })
  } catch (err: unknown) {
    throw toFriendlyNetworkError(err)
  }
  if (!res.ok) {
    const text = await res.text()
    const err = new Error(text || `HTTP ${res.status}`) as Error & { status: number }
    err.status = res.status
    throw err
  }
}

export async function getPublicCampaign(shortId: string): Promise<ApiPublicCampaignResponse> {
  if (E2E_MOCK_API) {
    return e2eMockApiFetch<ApiPublicCampaignResponse>(
      `/campaigns/${encodeURIComponent(shortId)}`,
      { method: 'GET' },
      null
    )
  }

  let res: Response
  try {
    res = await fetch(`${BASE_URL}/campaigns/${encodeURIComponent(shortId)}`, {
      headers: { 'Content-Type': 'application/json' },
    })
  } catch (err: unknown) {
    throw toFriendlyNetworkError(err)
  }
  if (!res.ok) {
    const text = await res.text()
    const err = new Error(text || `HTTP ${res.status}`) as Error & { status: number }
    err.status = res.status
    throw err
  }
  return res.json() as Promise<ApiPublicCampaignResponse>
}

// ─── Goals + Saver ────────────────────────────────────────────────────────────

export interface ApiGoalBucket {
  id: string
  name: string
  amount: number
  currency: string
}

export interface ApiSaver {
  id: string
  user_id: string
  amount: number
  currency: string
}

export function getMyGoals(): Promise<ApiGoalBucket[]> {
  return apiFetch<ApiGoalBucket[]>('/me/goals')
}

export function upsertGoals(buckets: { name: string; amount: number }[]): Promise<ApiGoalBucket[]> {
  return apiFetch<ApiGoalBucket[]>('/me/goals', {
    method: 'PUT',
    body: JSON.stringify({ buckets }),
  })
}

export function patchGoalBucket(name: string, data: { amount?: number }): Promise<ApiGoalBucket> {
  return apiFetch<ApiGoalBucket>(`/me/goals/${encodeURIComponent(name)}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export function getMySaver(): Promise<ApiSaver> {
  return apiFetch<ApiSaver>('/me/saver')
}

export function upsertMySaver(data: { amount: number }): Promise<ApiSaver> {
  return apiFetch<ApiSaver>('/me/saver', {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

// ─── Admin ────────────────────────────────────────────────────────────────────

export interface AdminStats {
  total_users: number
  max_users: number
  pending_applications: number
  approved_count: number
  rejected_count: number
  waitlist_count: number
  cohort_size: number
}

export function patchAdminConfig(
  key: string,
  value: string,
  valueType?: string
): Promise<{ key: string; value: string }> {
  return apiFetch(`/admin/config/${key}`, {
    method: 'PATCH',
    body: JSON.stringify({ value, value_type: valueType ?? 'int' }),
  })
}

export interface AdminCohortMember {
  id: string
  cohort_id: string
  user_id: string
  status: string
  created_at: string
  updated_at: string | null
  name: string
  surname: string
  email: string
  origin_city: string
  origin_country: string
}

export interface AdminCohortResponse {
  cohort: {
    id: string
    name: string
    destination: string
    max_participants: number
    status: string
    created_at: string
  } | null
  members: AdminCohortMember[]
}

export function getAdminStats(): Promise<AdminStats> {
  return apiFetch<AdminStats>('/admin/stats')
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  limit: number
}

export function getApplications(params?: {
  status?: string
  search?: string
  page?: number
  limit?: number
}): Promise<PaginatedResponse<ApiUser>> {
  const query = new URLSearchParams()
  if (params?.status) query.set('status', params.status)
  if (params?.search) query.set('search', params.search)
  if (params?.page) query.set('page', String(params.page))
  if (params?.limit) query.set('limit', String(params.limit))
  const qs = query.toString()
  return apiFetch<PaginatedResponse<ApiUser>>(`/admin/applications${qs ? '?' + qs : ''}`)
}

export function patchApplicationStatus(id: string, status: string): Promise<ApiUser> {
  return apiFetch<ApiUser>(`/admin/applications/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  })
}

export function getAdminCohort(): Promise<AdminCohortResponse> {
  return apiFetch<AdminCohortResponse>('/admin/cohort')
}

export function removeFromCohort(userId: string): Promise<void> {
  return apiFetch<void>(`/admin/cohort/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  })
}

export interface ApiWaitlistEntry {
  id: string
  email: string
  created_at: string
  status: string
  onboarding_id?: string
  goal?: string
  destination?: string
  timeline?: string
  origin_city?: string
  origin_country?: string
  age_range?: string
  savings_status?: string
  current_situation?: string
}

export function getAdminWaitlist(params?: {
  page?: number
  limit?: number
  status?: string
}): Promise<PaginatedResponse<ApiWaitlistEntry>> {
  const query = new URLSearchParams()
  if (params?.page) query.set('page', String(params.page))
  if (params?.limit) query.set('limit', String(params.limit))
  if (params?.status) query.set('status', params.status)
  const qs = query.toString()
  return apiFetch<PaginatedResponse<ApiWaitlistEntry>>(`/admin/waitlist${qs ? '?' + qs : ''}`)
}

// ─── Community Posts ──────────────────────────────────────────────────────────

export interface ApiCommunityMember {
  id: string
  user_id: string
  status: string
  name: string
  surname: string
  origin_city: string
  destination_city: string
}

export interface ApiCommunityMembersResponse {
  cohort_id: string
  members: ApiCommunityMember[]
}

export function getCommunityMembers(): Promise<ApiCommunityMembersResponse> {
  return apiFetch<ApiCommunityMembersResponse>('/community/members')
}

export interface ApiCommunityCampaign {
  id: string
  user_id: string
  short_id: string
  title: string
  name: string
  surname: string
  active_service_count: number
  is_published: boolean
  created_at: string
}

export function getCommunityCampaigns(): Promise<ApiCommunityCampaign[]> {
  return apiFetch<ApiCommunityCampaign[]>('/community/campaigns')
}

export interface ApiCommunityStats {
  approved_users_count: number
  approved_users_total_saved: number
  active_campaigns_count: number
}

export function getCommunityStats(): Promise<ApiCommunityStats> {
  return apiFetch<ApiCommunityStats>('/community/stats')
}

export interface ApiPost {
  id: string
  user_id: string
  author_name: string
  body: string
  created_at: string
  is_admin: boolean
  is_pinned: boolean
  like_count: number
  liked_by_me: boolean
  comment_count: number
}

export interface ApiComment {
  id: string
  post_id: string
  user_id: string
  author_name: string
  body: string
  created_at: string
  is_admin: boolean
  like_count: number
  liked_by_me: boolean
}

export interface ApiPostsResponse {
  posts: ApiPost[]
  next_cursor: { before_created_at: string; before_id: string } | null
}

export function getCommunityPosts(cursor?: {
  before_created_at: string
  before_id: string
}): Promise<ApiPostsResponse> {
  const params = cursor
    ? `?before_created_at=${encodeURIComponent(cursor.before_created_at)}&before_id=${cursor.before_id}&limit=20`
    : '?limit=20'
  return apiFetch<ApiPostsResponse>(`/community/posts${params}`)
}

export function searchCommunityPosts(
  query: string,
  cursor?: {
    before_created_at: string
    before_id: string
  }
): Promise<ApiPostsResponse> {
  const params = new URLSearchParams({ q: query.trim(), limit: '20' })
  if (cursor) {
    params.set('before_created_at', cursor.before_created_at)
    params.set('before_id', cursor.before_id)
  }
  return apiFetch<ApiPostsResponse>(`/community/posts/search?${params.toString()}`)
}

export function createPost(body: string): Promise<ApiPost> {
  return apiFetch<ApiPost>('/community/posts', {
    method: 'POST',
    body: JSON.stringify({ body }),
  })
}

export function deletePost(id: string): Promise<void> {
  return apiFetch<void>(`/community/posts/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export function getComments(postId: string): Promise<ApiComment[]> {
  return apiFetch<ApiComment[]>(`/community/posts/${encodeURIComponent(postId)}/comments`)
}

export function createComment(postId: string, body: string): Promise<ApiComment> {
  return apiFetch<ApiComment>(`/community/posts/${encodeURIComponent(postId)}/comments`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  })
}

export function deleteComment(id: string): Promise<void> {
  return apiFetch<void>(`/community/comments/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

// ─── Phase 2: Likes ───────────────────────────────────────────────────────────

export function toggleLike(postId: string): Promise<void> {
  return apiFetch<void>(`/community/posts/${encodeURIComponent(postId)}/like`, {
    method: 'POST',
  })
}

export function toggleCommentLike(commentId: string): Promise<void> {
  return apiFetch<void>(`/community/comments/${encodeURIComponent(commentId)}/like`, {
    method: 'POST',
  })
}

export function pinPost(postId: string, pinned: boolean): Promise<void> {
  return apiFetch<void>(`/admin/community/posts/${encodeURIComponent(postId)}/pin`, {
    method: 'PATCH',
    body: JSON.stringify({ pinned }),
  })
}

// ─── Phase 2: Notifications ───────────────────────────────────────────────────

export interface ApiNotification {
  id: string
  actor_name: string
  post_id: string
  type: 'comment' | 'like'
  read: boolean
  created_at: string
}

export function getNotifications(): Promise<ApiNotification[]> {
  return apiFetch<ApiNotification[]>('/me/notifications')
}

export function getNotificationCount(): Promise<{ unread?: number; count?: number }> {
  return apiFetch<{ unread?: number; count?: number }>('/me/notifications/count')
}

export function markNotificationsRead(): Promise<void> {
  return apiFetch<void>('/me/notifications/read', { method: 'PATCH' })
}

// ─── Meetups ──────────────────────────────────────────────────────────────────

export interface ApiCommunityMeetup {
  id: string
  title: string
  starts_at: string
  join_url: string | null
  attendee_count: number
  joined_by_me: boolean
}

export interface ApiAdminMeetup {
  id: string
  cohort_id: string
  title: string
  starts_at: string
  join_url: string
  attendee_count: number
  created_at: string
}

export interface ApiMeetupAttendee {
  user_id: string
  name: string
  surname: string
}

export function getCommunityMeetups(): Promise<ApiCommunityMeetup[]> {
  return apiFetch<ApiCommunityMeetup[]>('/community/meetups')
}

export function joinMeetup(id: string): Promise<void> {
  return apiFetch<void>(`/community/meetups/${encodeURIComponent(id)}/join`, { method: 'POST' })
}

export function cancelMeetup(id: string): Promise<void> {
  return apiFetch<void>(`/community/meetups/${encodeURIComponent(id)}/join`, { method: 'DELETE' })
}

export function getAdminMeetups(): Promise<ApiAdminMeetup[]> {
  return apiFetch<ApiAdminMeetup[]>('/admin/meetups')
}

export function createAdminMeetup(data: {
  title: string
  starts_at: string
  join_url: string
}): Promise<ApiAdminMeetup> {
  return apiFetch<ApiAdminMeetup>('/admin/meetups', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export function patchAdminMeetup(
  id: string,
  data: Partial<{ title: string; starts_at: string; join_url: string }>
): Promise<ApiAdminMeetup> {
  return apiFetch<ApiAdminMeetup>(`/admin/meetups/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export function deleteAdminMeetup(id: string): Promise<void> {
  return apiFetch<void>(`/admin/meetups/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export function getAdminMeetupAttendees(id: string): Promise<ApiMeetupAttendee[]> {
  return apiFetch<ApiMeetupAttendee[]>(`/admin/meetups/${encodeURIComponent(id)}/attendees`)
}
